import React, { useState } from 'react';
import { Card, Row, Col, Collapse, Alert, Table, Tag, Modal, Button } from 'antd';
import { motion } from 'framer-motion';
import { 
  ThunderboltOutlined,
  SafetyOutlined,
  ToolOutlined,
  FileTextOutlined,
  DollarOutlined
} from '@ant-design/icons';

const { Panel } = Collapse;

interface SpecialCargo {
  id: number;
  type: string;
  icon: React.ReactNode;
  color: string;
  requirements: string[];
  fees: string;
  notes: string;
  documents?: string[];
}

const specialCargos: SpecialCargo[] = [
  {
    id: 1,
    type: '带磁货物',
    icon: <ThunderboltOutlined />,
    color: '#faad14',
    requirements: [
      '每次出货必须去机场做磁检报告，费用350/票，不可瞒报',
      '对于重量段小的带磁货物，建议客户走带电渠道，规避磁检费用'
    ],
    fees: 'RMB 350/票 磁检费',
    notes: '如果是带磁性的走普货标快特快渠道需要加收此费用，走带电渠道无需加收',
    documents: ['磁检报告', 'MSDS文件']
  },
  {
    id: 2,
    type: '带少量液体、粉末的货物',
    icon: <SafetyOutlined />,
    color: '#1890ff',
    requirements: [
      '走带电渠道',
      '提供MSDS文件'
    ],
    fees: '无额外费用',
    notes: '必须通过带电专用渠道运输',
    documents: ['MSDS文件']
  },
  {
    id: 3,
    type: '带电货物',
    icon: <ThunderboltOutlined />,
    color: '#52c41a',
    requirements: [
      '分带锂离子电池、锂金属电池、干电池',
      '带锂离子电池，配套PI966，内置PI967；电池功率不能超过100W/H',
      '带锂金属电池，配套PI968，内置PI969',
      '干电池需要做好绝缘',
      '以上3种带电货物，提供有效的航空运输报告和UN38.3都可以走普货渠道；提供有效MSDS文件，可以走香港和韩国渠道'
    ],
    fees: '按带电渠道收费',
    notes: '所有带电产品（内置电池，配套电池）都是走带电渠道',
    documents: ['UN38.3报告', 'MSDS文件', 'PI966/967/968/969证书']
  },
  {
    id: 4,
    type: '刀具产品',
    icon: <ToolOutlined />,
    color: '#f5222d',
    requirements: [
      '需要提供公安局备案正本文件',
      '可以在广州飞'
    ],
    fees: '按普货收费',
    notes: '必须提供完整的公安局备案文件',
    documents: ['公安局备案正本文件']
  }
];

const additionalFeeData = [
  {
    key: '1',
    category: '渠道附加费',
    type: '出口报关费',
    fee: 'RMB300/票',
    description: '续页费RMB50/页'
  },
  {
    key: '2',
    category: '渠道附加费',
    type: '磁检费',
    fee: 'RMB350/票',
    description: '单票超出50件的，超出件数加收RMB1.5/件'
  },
  {
    key: '3',
    category: '渠道附加费',
    type: '带锂电池货物',
    fee: 'RMB1/KG',
    description: '提供航空运输报告走普货渠道加收电池操作费，MIN500/票'
  },
  {
    key: '4',
    category: '渠道附加费',
    type: '单票货清关品名超过5个',
    fee: 'RMB30/个',
    description: '超出的品名费；同类型产品按清关编码计算品名个数'
  },
  {
    key: '5',
    category: '渠道附加费',
    type: '客户自有税号做不包税',
    fee: 'USD85/票',
    description: '运费单询。目的港清关费USD85/票，ISC费用USD220-260/票(以航司收费为准)'
  }
];

const productFeeData = [
  {
    key: '1',
    category: '产品附加费',
    type: '+1/KG',
    products: [
      '纺织品类：服装、鞋帽、手套、包包、线包、床单被套、毯子',
      '工具类：修理工具、尺子类、磨刀器、测距仪、测量仪、雕刻工具',
      '电子产品：适配器、充电器、扫描、网卡、转接头、摄像头',
      '玩具类'
    ]
  },
  {
    key: '2',
    category: '产品附加费',
    type: '+2/KG',
    products: [
      '摄像机、手表类、智能手环、3D/激光打印机、雕刻机、眼镜、电动牙刷、刮刀器、化妆品、刀具',
      '成人用品、按摩器、美容仪器、木制品、太阳能灯（板）、带少量液体、粉末美产品'
    ]
  },
  {
    key: '3',
    category: '拒收产品',
    type: '不接',
    products: [
      '纯电池、移动电源、无人机、电脑、手机、食品类、药品类、医疗类产品不接，其他反倾销产品不接'
    ]
  }
];

const feeColumns = [
  {
    title: '类别',
    dataIndex: 'category',
    key: 'category',
    width: 120,
  },
  {
    title: '项目',
    dataIndex: 'type',
    key: 'type',
    width: 200,
  },
  {
    title: '费用',
    dataIndex: 'fee',
    key: 'fee',
    width: 120,
    render: (text: string) => <Tag color="orange">{text}</Tag>
  },
  {
    title: '说明',
    dataIndex: 'description',
    key: 'description',
  }
];

const productColumns = [
  {
    title: '类别',
    dataIndex: 'category',
    key: 'category',
    width: 120,
  },
  {
    title: '附加费',
    dataIndex: 'type',
    key: 'type',
    width: 100,
    render: (text: string) => {
      const color = text === '不接' ? 'red' : text === '+2/KG' ? 'orange' : 'blue';
      return <Tag color={color}>{text}</Tag>;
    }
  },
  {
    title: '产品类型',
    dataIndex: 'products',
    key: 'products',
    render: (products: string[]) => (
      <div>
        {products.map((product, index) => (
          <div key={index} style={{ marginBottom: '4px', fontSize: '13px' }}>
            {typeof product === 'string' ? product : product}
          </div>
        ))}
      </div>
    )
  }
];

const SpecialCargoRequirements: React.FC = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedCargo, setSelectedCargo] = useState<SpecialCargo | null>(null);

  const handleCargoClick = (cargo: SpecialCargo) => {
    setSelectedCargo(cargo);
    setModalVisible(true);
  };

  return (
    <section id="special-cargo" style={{ 
      padding: '80px 0', 
      background: '#f8f9fa'
    }}>
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          style={{ textAlign: 'center', marginBottom: '60px' }}
        >
          <h2 className="display-medium" style={{ 
            marginBottom: '24px',
            color: '#1d1d1f',
          }}>
            特殊货物收货要求
          </h2>
          <p className="body-large" style={{ 
            color: '#86868B', 
            maxWidth: '800px', 
            margin: '0 auto',
            fontSize: '1.125rem'
          }}>
            专业处理各类特殊货物，确保合规运输和安全清关
          </p>
        </motion.div>

        {/* 特殊货物类型 */}
        <Row gutter={[24, 24]} style={{ marginBottom: '60px' }}>
          {specialCargos.map((cargo, index) => (
            <Col xs={24} sm={12} lg={6} key={cargo.id}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card
                  hoverable
                  onClick={() => handleCargoClick(cargo)}
                  style={{
                    height: '100%',
                    borderRadius: '16px',
                    border: `2px solid ${cargo.color}`,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  bodyStyle={{ padding: '24px', textAlign: 'center' }}
                >
                  <div style={{
                    fontSize: '48px',
                    color: cargo.color,
                    marginBottom: '16px'
                  }}>
                    {cargo.icon}
                  </div>
                  
                  <h3 style={{ 
                    color: '#1D1D1F',
                    marginBottom: '12px',
                    fontSize: '1.125rem',
                    fontWeight: '600'
                  }}>
                    {cargo.type}
                  </h3>
                  
                  <Tag color={cargo.color} style={{ marginBottom: '12px' }}>
                    {cargo.fees}
                  </Tag>
                  
                  <p style={{ 
                    color: '#666',
                    fontSize: '0.875rem',
                    lineHeight: '1.4',
                    margin: 0
                  }}>
                    点击查看详细要求
                  </p>
                </Card>
              </motion.div>
            </Col>
          ))}
        </Row>

        {/* 附加费用表格 */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          style={{ marginBottom: '40px' }}
        >
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <DollarOutlined style={{ color: '#faad14' }} />
                <span>专线附加费</span>
              </div>
            }
            style={{
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
            }}
          >
            <Alert
              message="渠道附加费"
              description="以下为专线渠道可能产生的各类附加费用，请在发货前详细了解"
              type="info"
              showIcon
              style={{ marginBottom: '24px' }}
            />
            
            <Table
              columns={feeColumns}
              dataSource={additionalFeeData}
              pagination={false}
              size="small"
              style={{ marginBottom: '32px' }}
            />

            <Alert
              message="产品附加费"
              description="不同产品类型可能产生的附加费用"
              type="warning"
              showIcon
              style={{ marginBottom: '16px' }}
            />
            
            <Table
              columns={productColumns}
              dataSource={productFeeData}
              pagination={false}
              size="small"
            />
          </Card>
        </motion.div>

        {/* UPS/FedEx附加费说明 */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FileTextOutlined style={{ color: '#1890ff' }} />
                <span>UPS、FedEx附加费说明</span>
              </div>
            }
            style={{
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
            }}
          >
            <Collapse>
              <Panel header="FEDEX/UPS派送附加费" key="1">
                <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
                  <p><strong>1. 超重附加费：</strong>单箱毛重超过22.5KG，加收超重费RMB250/箱（快递限重40KG/箱，非纸箱货不可走快递）</p>
                  <p><strong>2. 超尺寸附加费一：</strong>最长边长度≥121cm，或者第二最长边≥76cm，或者周长≥265cm，（周长=长+宽*2+高*2），加收RMB145/箱</p>
                  <p><strong>3. 超尺寸附加费二：</strong>最长边≥200cm或周长≥330cm，（周长=长+宽*2+高*2），加收RMB1300/箱（周长最长不超过400CM）</p>
                  <p><strong>4. 住宅费用：</strong>快递派送住宅地址另外加收RMB25/箱</p>
                  <p><strong>5. 偏远费用：</strong>偏远地区附加费RMB20/箱，超偏远地区附加费RMB30/箱；超级超级偏远RMB60/箱</p>
                  <p><strong>6. 地址修正费用：</strong>如果提供的地址或者派送邮编不准，导致实际派送有更改，更改费用USD28/箱</p>
                  <p style={{ margin: 0 }}><strong>7. 异型包装附加费：</strong>因包装问题被判定异形包装，收取USD25/箱，提供对应账单凭证</p>
                </div>
              </Panel>
              
              <Panel header="赔偿说明" key="2">
                <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
                  <p><strong>1. 延误赔偿：</strong>入仓后第二天开始起算，超10天没有提取，按照RMB1/KG/天做延误赔偿，最高赔偿RMB10/KG。因海关查验、飞机故障延误、航班取消、天气原因等不可抗力因素造成的延误不计算在延误赔偿里面</p>
                  <p><strong>2. 丢件赔偿：</strong>运输和派送途中丢货，最高补偿RMB60/KG，不退运费；上网后提取未签收，按照快递赔偿，最高赔偿USD100/件不退运费。高价值产品建议客户独购买保险我司提供保险服务和提交保险索赔资料</p>
                  <p style={{ margin: 0 }}><strong>3. 货物签收后：</strong>发现箱内货物数量短缺或上架数量缺少的情况，我司不接受理赔</p>
                </div>
              </Panel>
            </Collapse>
          </Card>
        </motion.div>

        {/* 特殊货物详情模态框 */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {selectedCargo?.icon}
              <span>{selectedCargo?.type} 详细要求</span>
            </div>
          }
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setModalVisible(false)}>
              关闭
            </Button>
          ]}
          width={600}
        >
          {selectedCargo && (
            <div>
              <Alert
                message="收货要求"
                type="info"
                showIcon
                style={{ marginBottom: '16px' }}
              />
              <ul style={{ paddingLeft: '20px', marginBottom: '20px' }}>
                {selectedCargo.requirements.map((req, index) => (
                  <li key={index} style={{ marginBottom: '8px', lineHeight: '1.5' }}>
                    {req}
                  </li>
                ))}
              </ul>

              {selectedCargo.documents && (
                <>
                  <Alert
                    message="需要文件"
                    type="warning"
                    showIcon
                    style={{ marginBottom: '12px' }}
                  />
                  <div style={{ marginBottom: '20px' }}>
                    {selectedCargo.documents.map((doc, index) => (
                      <Tag key={index} color="blue" style={{ marginBottom: '4px' }}>
                        {doc}
                      </Tag>
                    ))}
                  </div>
                </>
              )}

              <Alert
                message="费用说明"
                description={selectedCargo.fees}
                type="success"
                showIcon
                style={{ marginBottom: '16px' }}
              />

              <Alert
                message="注意事项"
                description={selectedCargo.notes}
                type="warning"
                showIcon
              />
            </div>
          )}
        </Modal>
      </div>
    </section>
  );
};

export default SpecialCargoRequirements;
